import React, { useCallback } from 'react'
import { FileInput, maxLength, useNotify, useRecordContext } from 'react-admin'
import { useWatch, useFormState } from 'react-hook-form'
import { COMMON_FIELDS, NEWS_FIELDS } from '../../providers/localization/constant'
import { styled } from '@mui/material'
import { translateErrMessageDropZone } from '../../utils/errorMessage'
import { FilePreviewProps, SigleFileInputProps } from './type'
import { FileField } from '../fields'

const defaultAcceptFiles = 'image/png, image/jpg, image/jpeg, application/pdf'
const defaultFileInputOptions = {
  maxFiles: 5,
}

const FileInputStyled = styled(FileInput)(({ theme }) => ({
  color: theme.palette.grey[600],
  '& .RaFileInputPreview-removeButton': {
    zIndex: 1,
    position: 'absolute',
  },
}))

const FilePreview: React.FC<FilePreviewProps> = (props) => {
  const { source, fallbackSource, title } = props
  const record = useRecordContext()
  const { src: uploadSrc } = record
  const path = uploadSrc ? source : fallbackSource
  const titleFallback = uploadSrc ? 'title' : title
  return <FileField source={path} title={titleFallback} target='_blank' />
}

export const SigleFileInput: React.FC<SigleFileInputProps> = ({
  isApproved = false,
  maxSize = 10000000,
  source = 'file',
  accept = defaultAcceptFiles,
  fileInputProps,
  sx = {},
  fallbackSource = null,
  ...props
}) => {
  const { maxFiles, ...otherProps } = { ...defaultFileInputOptions, ...fileInputProps?.options }
  const notify = useNotify()
  const files = useWatch({ name: source })
  const { errors } = useFormState()
  const disabled = files?.length >= maxFiles || isApproved

  const disableStyle = disabled
    ? {
        '& .RaFileInput-dropZone': {
          cursor: 'default',
          background: 'transparent',
          border: '1px solid',
          borderColor: errors.files ? 'error.main' : 'common.black',
        },
      }
    : {}

  const baseStyle = {
    '& .MuiFormHelperText-root, .RaLabeled-label, .RaFileInput-dropZone > *': {
      color: errors.files ? 'error.main' : 'unset',
    },
    ...disableStyle,
  }
  const onDropRejected = useCallback(
    (fileRejections): void => {
      notify(translateErrMessageDropZone(fileRejections[0].errors[0].code), { type: 'warning' })
    },
    [notify],
  )

  return (
    <FileInputStyled
      source={source}
      helperText={`${COMMON_FIELDS}.file.helper`}
      label={`${NEWS_FIELDS}.file`}
      inputProps={{}}
      options={{
        disabled,
        maxFiles,
        onDropRejected,
        ...otherProps,
      }}
      labelMultiple={
        disabled ? 'ra.input.file.upload_max_file_reach' : 'ra.input.file.upload_several'
      }
      maxSize={maxSize}
      accept={accept}
      validate={[maxLength(maxFiles, 'ra.notification.file_count_exceed_error')]}
      sx={{ ...baseStyle, ...disableStyle, ...sx }}
      {...props}
    >
      <FilePreview source='src' title='name' fallbackSource={fallbackSource} />
    </FileInputStyled>
  )
}
